import Data from './index'
//import RePoE from './data.min.json';
import { Game, UniqueProfile, getApiPath, getAssetsPath } from '@/common'
import { validateUniques } from '@/logic/validation'
import { ProfileApiImport } from '@/types/api'
import { PlayerClass } from '@/types/profile'
import crc32 from 'crc-32'
import { ItemFlags } from './enums'
import { initTrans } from './trans'
import {
  ModDefinition,
  PassiveSkillDefinition,
  PassiveSkillWithRange,
  PassiveTreeData,
  PassiveTreeDefinition,
  PassiveTreeGroup,
  PassiveTreeLegacyNode,
  PassiveTreeNode,
  UniqueDefinition
} from './types'

function passiveSkill(
  node: PassiveTreeNode | PassiveTreeLegacyNode,
  skills: Record<string, PassiveSkillDefinition> | undefined
) {
  if ('skill_id' in node) {
    return skills![node.skill_id]
  } else {
    return node
  }
}

function processTree(
  data: PassiveTreeDefinition<PassiveTreeNode | PassiveTreeLegacyNode>,
  atlas: number,
  skills: Record<string, PassiveSkillDefinition> | undefined
) {
  const { nodes, groups, root_passives } = data

  if (!atlas) {
    data.characterRoot = {}
    data.ascendancyRoot = {}
  }

  for (const group of Object.values(groups)) {
    group.nodes = []
  }

  for (const [id, node] of Object.entries(nodes)) {
    const nid = Number(id)

    const skill = passiveSkill(node, skills)
    if ('skill_id' in node) {
      skill.used = true
      // compatibility with old code. remove when migrated to TS
      // Object.assign(node, skill)
    } else {
      const baseNode = (
        atlas ? (atlas === 2 && Data.atlas_passive_tree_league) || Data.atlas_passive_tree : Data.passive_tree
      )?.nodes[nid]
      if (baseNode) node.ref_id = baseNode.skill_id
    }

    if (skill.mastery) {
      if (!skill.mastery.count_stat) {
        skill.mastery.count_stat = skill.name
      }
    }

    groups[node.parent]?.nodes.push(nid)

    if (!atlas) {
      if (skill.starting_node) {
        const chr = Object.keys(Data.characters).find((id) => Data.characters[id].name === skill.starting_node)
        if (chr) {
          data.characterRoot[chr] = nid
          skill.starting_node = chr as PlayerClass
        } else {
          delete skill.starting_node
        }
      } else if (skill.is_ascendancy_root) {
        data.ascendancyRoot[skill.ascendancy!] = nid
      }
    }

    for (const oid of node.connections) {
      const onode = nodes[oid]
      if (onode && !onode.connections.includes(nid)) {
        onode.connections.push(nid)
      }
    }
  }

  const choice_groups = new Set<PassiveTreeGroup>()
  for (const group of Object.values(groups)) {
    if (group.bg) continue
    group.bg = 0
    for (const id of group.nodes) {
      const node = nodes[id]
      if (node.radius <= 3) {
        group.bg = Math.max(group.bg, node.radius)
      }
      const skill = passiveSkill(node, skills)
      if (skill.ascendancy) {
        group.ascendancy = skill.ascendancy
      } else if (skill.is_ascendancy_root) {
        group.ascendancy = 'unknown'
      } else if (skill.is_multiple_choice_option) {
        choice_groups.add(group)
      }
    }
    if (group.ascendancy) {
      group.bg = 0
    }
    if (group.bg > 3) group.bg = 0
  }
  for (const group of choice_groups) {
    for (const id of group.nodes) {
      const node = nodes[id]
      const skill = passiveSkill(node, skills)
      if (skill.is_multiple_choice_option) {
        const parentNode = nodes[node.connections[0]]
        const parentGroup = groups[parentNode?.parent]
        if (parentGroup?.ascendancy) group.ascendancy = parentGroup.ascendancy
      }
    }
    if (group.ascendancy) {
      group.bg = 0
    }
  }
}

function init(RePoE: any) {
  Object.assign(Data, RePoE)

  for (const [id, chr] of Object.entries(Data.characters)) {
    for (const aid of Object.keys(chr.ascendancies)) {
      Data.ascendancyClass[aid] = id as PlayerClass
    }
  }

  for (const essence of Object.values(Data.essences)) {
    for (const cls of Object.values(essence.mods)) {
      let mod = Data.mods[cls]
      if (!mod) continue
      if (!mod.is_essence_only) {
        mod.essence_version = `${cls}_Essence`
        mod = { ...mod }
        mod.spawn_weights = [{ tag: 'default', weight: 0 }]
        mod.generation_weights = []
        mod.is_essence_only = true
        mod.non_essence_version = cls
        Data.mods[mod.essence_version!] = mod
      }
      mod.name = essence.name
    }
  }

  for (const opt of Data.crafting_bench_options) {
    opt.id = Object.entries(opt.actions)
      .map(([key, value]) => `${key}=${value}`)
      .join('&')
  }

  const armorTypes = [0, 1, 2, 4, 3, 5, 6, 7]

  for (const [key, list] of Object.entries(Data.icons)) {
    Data.iconMap[key] = Object.fromEntries(list.map((name, index) => [name, index]))
  }

  for (const [id, item] of Object.entries(Data.items)) {
    item.id = id

    Data.itemByName[item.name] = item
    Data.itemByType[item.item_class] = Data.itemByType[item.item_class] || []
    Data.itemByType[item.item_class].push(item)

    item.hash = (crc32.str(id) >>> 0).toString(16).padStart(8, '0')
    if (Data.itemByHash[item.hash]) {
      console.error(`hash collision: ${item.name} vs ${Data.itemByHash[item.hash].name}`)
    }
    Data.itemByHash[item.hash] = item

    // not sure what this is for
    item.match = {
      icon: `art/2ditems/${item.visual_identity.dds_file}.png`,
      name: new RegExp('\\b' + item.name.replace(/[$-.[-^?|]/g, '\\$&') + '\\b')
    }

    let armorType = 0
    if (item.properties) {
      if (item.properties.armour) armorType += 1
      if (item.properties.evasion) armorType += 2
      if (item.properties.energy_shield) armorType += 4
    }
    item.armour_type = armorTypes[armorType]

    if (item.tattoo) {
      Data.tattoos[item.tattoo.id].item = id
    }
  }
  for (const items of Object.values(Data.itemByType)) {
    items.sort((a, b) => a.drop_level - b.drop_level)
  }

  if (Data.item_remap) {
    for (const [src, dst] of Object.entries(Data.item_remap)) {
      if (Data.items[dst]) {
        const hash = (crc32.str(src) >>> 0).toString(16).padStart(8, '0')
        Data.itemByHash[hash] = Data.items[dst]
      }
    }
  }

  for (const [id, gem] of Object.entries(Data.gems)) {
    if (!gem.base_item) continue
    if (gem.active_skill?.transfigured === 4) {
      gem.active_skill.transfigured = 0
    }
    const item = Data.items[gem.base_item.id]
    if (item) {
      item.skills = item.skills ?? []
      item.skills.push(id)
      ;(item as any).skill = id
    }
  }
  for (const item of Object.values(Data.items)) {
    if (item.skills)
      item.skills.sort(
        (a, b) => (Data.gems[a].active_skill?.transfigured ?? 0) - (Data.gems[b].active_skill?.transfigured ?? 0)
      )
  }

  const influenceList = [
    ItemFlags.Shaper,
    ItemFlags.Elder,
    ItemFlags.Crusader,
    ItemFlags.Redeemer,
    ItemFlags.Hunter,
    ItemFlags.Warlord
  ]

  const influenceTags: Record<string, ItemFlags> = {}
  for (const cls of Object.values(Data.item_classes)) {
    if (cls.influence) {
      cls.influence.forEach((tag, type) => (influenceTags[tag] = influenceList[type]))
    }
  }

  const statOrder: Record<string, number> = {}
  let nextOrder = 0
  for (const domain of Data.translations) {
    if (domain.name !== 'global' && domain.name !== 'tincture' && domain.name !== 'map') continue
    for (const trans of domain.data) {
      if (trans.hidden) continue
      for (const id of trans.ids) {
        statOrder[id] = ++nextOrder
      }
    }
  }

  const modList: {
    mod: ModDefinition
    order: number[]
  }[] = []
  for (const mod of Object.values(Data.mods)) {
    mod.visible = false
    mod.stats = mod.stats.filter((stat) => stat.min !== 0 || stat.max !== 0)
    mod.influence = 0
    for (const w of mod.spawn_weights) {
      if (influenceTags[w.tag]) mod.influence |= influenceTags[w.tag]
    }
    if (!mod.group && mod.groups?.length) {
      mod.group = mod.groups[0]
    }
    modList.push({
      mod,
      order: mod.stats
        .map((stat) => {
          if (statOrder[stat.id]) mod.visible = true
          return statOrder[stat.id] || 0
        })
        .sort((a, b) => a - b)
    })
  }
  modList.sort((a, b) => {
    if (a.mod.generation_type !== b.mod.generation_type) {
      return a.mod.generation_type.localeCompare(b.mod.generation_type)
    }
    for (let i = 0; i < a.order.length || i < b.order.length; ++i) {
      const ia = a.order[i] || 0,
        ib = b.order[i] || 0
      if (ia !== ib) return ia - ib
    }
    return (a.mod.required_level || 0) - (b.mod.required_level || 0)
  })
  modList.forEach(({ mod }, index) => (mod.order = index + 1))

  initTrans(Data)
}

function createAlternateSkills() {
  for (const skill of Data.alternate_passive_skills) {
    const entry: PassiveSkillWithRange = {
      name: skill.name,
      icon: skill.icon,
      stats: Object.fromEntries(skill.stats.map(({ id, min, max }) => [id, { min, max }])),
      alternate: Data.alternate_tree_versions[skill.version].id
    }
    if (skill.flavour_text) entry.flavour_text = skill.flavour_text
    if (skill.types & 4) entry.is_notable = true
    if (skill.types & 8) entry.is_keystone = true
    Data.timeless_skills[skill.id] = entry
  }
}

const _treeLoad: Record<number, Promise<PassiveTreeData>> = {}

export function loadTree(version: number | string) {
  version = Number(version)
  if (version in _treeLoad) return _treeLoad[version]
  return (_treeLoad[version] = fetch(getAssetsPath(`tree${version}.json`))
    .then((r) => r.json())
    .then((data: PassiveTreeData) => {
      const skills = 'passive_skills' in data ? data.passive_skills : undefined
      processTree(data.passive_tree, 0, skills)
      processTree(data.atlas_passive_tree, 1, skills)
      if (data.atlas_passive_tree_league) {
        processTree(data.atlas_passive_tree_league, 2, skills)
      }
      // @ts-ignore for legacy code
      Data[`tree${version}`] = data
      Data.trees[version as number] = data
      return data
    }))
}

async function doLoadData() {
  const RePoE = await fetch(getAssetsPath('data.min.json')).then((r) => r.json())
  init(RePoE)
  const tree = await loadTree(Data.defaultVersion)
  Object.assign(Data, tree)
  createAlternateSkills()
}

let loading: Promise<void> | undefined
export function loadData() {
  if (!loading) loading = doLoadData()
  return loading
}

let uniquesLoading: Promise<Record<string, UniqueDefinition>> | undefined
async function doLoadUniques() {
  const dataLoaded = loadData()
  const response = (await fetch(getApiPath(`profiles/load/${Game}/${UniqueProfile}`)).then((r) =>
    r.json()
  )) as ProfileApiImport
  await dataLoaded
  const uniques = validateUniques(JSON.parse(response.data))
  Data.uniques = uniques
  return uniques
}
export function loadUniques() {
  if (!uniquesLoading) uniquesLoading = doLoadUniques()
  return uniquesLoading
}
